/* eslint-disable import/named */
/* eslint-disable new-cap */

import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Company } from '../../lib/models/company';
import { SymbolAndOptionalExchangeCode } from '../../lib/models/fundamental/types';
import {
  GWMarketDataQueryBucket,
  GWMarketDataSummaryBucket,
  OHLCVDataRow,
  OHLCVStateData,
} from '../../lib/models/gateway/types';
import { RootState } from '../../lib/store';
import { getCompany } from '../../lib/store/fundamental';
import { resetMarketDataQuery } from '../../lib/store/gateway';
import {
  advancedChartSubscription,
  chartSubscription,
  instrumentSnapshotSubscription,
  marketDataSubscribeTradeSubscription,
} from '../../lib/store/market-data/subscriptionHelpers';
import { getCompany as companyInfo, hasValidCookie } from '../../lib/store/selectors';
import { RegExStrings } from '../../lib/util/DataHelpers';
import { getDateTime } from '../../lib/util/temporalDeadZoneFunctions';

import {
  ChartingLibraryWidgetOptions,
  LanguageCode,
  ResolutionString,
  TimeFrameType,
  widget,
} from '../../charting_library';
import { useIsMobile } from '../../hooks';

import Datafeed from './datafeed';

import styles from './ChartWrapper.module.scss';

const INITIAL_MINUTE_HISTORY_DAYS = 10;
const INITIAL_DAY_HISTORY_BUFFER_DAYS = 90;

const getLanguageFromURL = (): LanguageCode | null => {
  const regex = new RegExp(RegExStrings.languageCode);
  const results = regex.exec(location.search);
  return results === null
    ? null
    : (decodeURIComponent(results[1].replace(RegExStrings.plusChar, ' ')) as LanguageCode);
};

export const TVChartContainer = () => {
  const { name } = useParams<'name'>();
  const symbolName = name!;
  const chartContainerRef = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const hasCookieSession = useSelector<RootState, boolean>((state: RootState) => hasValidCookie(state));
  const company = useSelector<RootState, Company | undefined>((state: RootState) =>
    companyInfo(symbolName, state));
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { isMobile } = useIsMobile();
  const { symbol, minuteData, hourData, dayData } = useSelector<
    RootState,
    {
      symbol: string;
      minuteData: OHLCVStateData;
      hourData: OHLCVStateData;
      dayData: OHLCVStateData;
    }
  >((state: RootState) => state.gateway.ohlcvData);

  const [chartMinuteData, setChartMinuteData] = useState<OHLCVDataRow[]>([]);
  const [chartHourData, setChartHourData] = useState<OHLCVDataRow[]>([]);
  const [chartDayData, setChartDayData] = useState<OHLCVDataRow[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const isInitialSubscriptionMade = useRef<string | null>(null);

  useEffect(() => {
    if (!hasCookieSession || !company || isInitialSubscriptionMade.current === symbolName) {
      return;
    }

    // temporary solution for subscription after refresh
    const mappedSymbol: SymbolAndOptionalExchangeCode = {
      symbol: symbolName,
      stockExchangeCode: company?.stockExchangeCode,
      countryDesignation: company?.countryDesignation,
    };

    const mappedSymbols: SymbolAndOptionalExchangeCode[] = [mappedSymbol];
    instrumentSnapshotSubscription(mappedSymbols);
    marketDataSubscribeTradeSubscription(mappedSymbols);
    chartSubscription(
      mappedSymbols,
      [GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths],
      false,
      true,
    );

    const endTime = getDateTime();
    if (!minuteData.data.length || symbol !== symbolName) {
      const startTime = getDateTime(true, ['minus'], [[INITIAL_MINUTE_HISTORY_DAYS, 'days']]);
      advancedChartSubscription(
        mappedSymbols,
        [GWMarketDataQueryBucket.OneMinute],
        startTime,
        endTime,
      );
    }
    if (!hourData.data.length || symbol !== symbolName) {
      const startTime = getDateTime(
        true,
        ['minus', 'plus'],
        [
          [90, 'days'],
          [60, 'minutes'],
        ],
      );
      advancedChartSubscription(
        mappedSymbols,
        [GWMarketDataQueryBucket.OneHour],
        startTime,
        endTime,
      );
    }
    if (!dayData.data.length || symbol !== symbolName) {
      const startTime = getDateTime(
        true,
        ['minus', 'minus', 'plus'],
        [
          [5, 'years'],
          [INITIAL_DAY_HISTORY_BUFFER_DAYS, 'days'],
          [1, 'day'],
        ],
      );
      advancedChartSubscription(
        mappedSymbols,
        [GWMarketDataQueryBucket.OneDay],
        startTime,
        endTime,
      );
    }

    isInitialSubscriptionMade.current = symbolName;
    // eslint-disable-next-line consistent-return
    return () => {
      dispatch(resetMarketDataQuery());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCookieSession, company, symbolName]);

  useEffect(() => {
    if (minuteData.data.length) {
      setChartMinuteData([...minuteData.data]);
    }
  }, [minuteData]);

  useEffect(() => {
    if (dayData.data.length) {
      setChartDayData([...dayData.data]);
    }
  }, [dayData]);

  useEffect(() => {
    if (hourData.data.length) {
      setChartHourData([...hourData.data]);
    }
  }, [hourData]);

  useEffect(() => {
    if (!company) {
      dispatch(getCompany(symbolName));
    }
  }, [company, dispatch, symbolName]);

  useEffect(() => {
    setShowLoader(!isDataLoaded);
  }, [isDataLoaded]);

  useEffect(() => {
    if (
      company
      && chartMinuteData.length
      && chartDayData.length
      && chartHourData
      && !isDataLoaded
    ) {
      const widgetOptions: ChartingLibraryWidgetOptions = {
        symbol: symbolName,
        datafeed: Datafeed(chartMinuteData, chartHourData, chartDayData, dispatch, company),
        interval: '5' as ResolutionString,
        container: chartContainerRef.current,
        custom_translate_function: (key) => {
          if (key === 'Script name') {
            return 'Choose indicator';
          }
          return null;
        },
        debug: true,
        locale: getLanguageFromURL() || 'en',
        client_id: 'tradingview.com',
        user_id: 'public_user_id',
        fullscreen: !isMobile,
        autosize: true,
        // Ensure the library path works with any base path (dev/build/deploy)
        library_path: `${import.meta.env.BASE_URL}charting_library/`,
        overrides: {
          priceScaleSelectionStrategyName: 'right',
        },
        loading_screen: {
          backgroundColor: '#0C2333',
        },
        disabled_features: ['header_fullscreen_button', 'header_symbol_search', 'header_compare'],
        studies_access: {
          type: 'black',
          tools: [{ name: '52 Week High/Low' }, { name: 'Script name' }],
        },
        time_frames: [
          { text: '5Y', resolution: '1M' as ResolutionString, description: '5 Years' },
          { text: '1Y', resolution: '1W' as ResolutionString, description: '1 Year' },
          { text: '3M', resolution: '1D' as ResolutionString, description: '3 Months' },
          { text: '1M', resolution: '60' as ResolutionString, description: '1 Month' },
          { text: '1W', resolution: '15' as ResolutionString, description: '1 Week' },
          { text: '1D', resolution: '5' as ResolutionString, description: '1 Day' },
        ],
        // Make custom CSS path base-aware as well
        custom_css_url: `${import.meta.env.BASE_URL}styles/chartStyle.css`,
        theme: 'dark',
      };
      const tvWidget = new widget(widgetOptions);
      setIsDataLoaded(true);

      tvWidget.onChartReady(() => {
        const volumeStudyId = tvWidget
          .activeChart()
          .getAllStudies()
          .find((x) => x.name === 'Volume')?.id;
        if (volumeStudyId) {
          const volume = tvWidget.activeChart().getStudyById(volumeStudyId);
          volume.applyOverrides({
            'volume.color.0': 'rgba(255, 10, 239, 0.1)',
            'volume.color.1': 'rgba(59, 176, 255, 0.1)',
          });
        }

        tvWidget.chart().getSeries().setChartStyleProperties(1, {
          upColor: '#ffffff',
          downColor: '#ffffff',
        });
        tvWidget.applyOverrides({
          'mainSeriesProperties.candleStyle.downColor': '#FF0AEF',
          'mainSeriesProperties.candleStyle.borderDownColor': '#FF0AEF',
          'mainSeriesProperties.candleStyle.wickDownColor': '#FF0AEF',
          'mainSeriesProperties.candleStyle.upColor': '#3BB0FF',
          'mainSeriesProperties.candleStyle.borderUpColor': '#3BB0FF',
          'mainSeriesProperties.candleStyle.wickUpColor': '#3BB0FF',
          'paneProperties.backgroundGradientStartColor': '#0E1A60',
          'paneProperties.backgroundGradientEndColor': '#181C37',
          'crossHairProperties.color': '#FDFFFF',
          'paneProperties.vertGridProperties.color': 'rgba(59, 176, 255, 0.1)',
          'paneProperties.horzGridProperties.color': 'rgba(59, 176, 255, 0.1)',
          'paneProperties.separatorColor': '#1D3960',
          'scalesProperties.lineColor': '#1D3960',
          'scalesProperties.textColor': '#FDFFFF',
          'scalesProperties.crosshairLabelBgColorDark': '#0C2333',
          'mainSeriesProperties.areaStyle.color1': 'rgba(59, 176, 255, 0.4)',
          'mainSeriesProperties.areaStyle.color2': 'rgba(59, 176, 255, 0)',
          'mainSeriesProperties.areaStyle.linecolor': '#3BB0FF',
        });

        const chart = tvWidget.chart();
        chart.setTimeFrame({
          val: { type: 'period-back' as TimeFrameType.PeriodBack, value: '1D' },
          res: '5' as ResolutionString,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolName, company, chartMinuteData, chartHourData, chartDayData]);

  return (
    <>
      <div
        ref={chartContainerRef}
        className={'TVChartContainer'}
        style={isMobile ? { height: `${window.innerHeight - 51}px`, width: '100%' } : {}}
      />
      {showLoader && (
        <div className={styles.loadingIndicator}>
          <div className={styles.spinner} />
        </div>
      )}
    </>
  );
};
